/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AddCredentialMappingPathParameters,
  GetCredentialMappingsPathParameters,
  RemoveCredentialMappingPathParameters,
  TLAddCredentialMappingRequest,
  TLCredentialMappingResponse,
  TLCredentialMappingsResponse,
  TLHTTPValidationError,
  TLSuccessResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Credential Mappings
 */
export const getCredentialMappings = (
  { credentialId }: GetCredentialMappingsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialMappingsResponse>(
    { url: `/connector-credentials/${credentialId}/mappings`, method: 'GET', signal },
    options,
  );
};

export const getGetCredentialMappingsQueryKey = ({ credentialId }: GetCredentialMappingsPathParameters) => {
  return ['connector-credentials', credentialId, 'mappings'] as const;
};

export const getGetCredentialMappingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getCredentialMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: GetCredentialMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCredentialMappingsQueryKey({ credentialId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCredentialMappings>>> = ({ signal }) =>
    getCredentialMappings({ credentialId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCredentialMappingsQueryResult = NonNullable<Awaited<ReturnType<typeof getCredentialMappings>>>;
export type GetCredentialMappingsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCredentialMappings<
  TData = Awaited<ReturnType<typeof getCredentialMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialMappingsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialMappings>>,
          TError,
          Awaited<ReturnType<typeof getCredentialMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialMappings<
  TData = Awaited<ReturnType<typeof getCredentialMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialMappings>>,
          TError,
          Awaited<ReturnType<typeof getCredentialMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialMappings<
  TData = Awaited<ReturnType<typeof getCredentialMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Credential Mappings
 */

export function useGetCredentialMappings<
  TData = Awaited<ReturnType<typeof getCredentialMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: GetCredentialMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCredentialMappingsQueryOptions({ credentialId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Credential Mappings
 */
export const prefetchGetCredentialMappingsQuery = async <
  TData = Awaited<ReturnType<typeof getCredentialMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: GetCredentialMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCredentialMappingsQueryOptions({ credentialId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Credential Mappings
 */
export const invalidateGetCredentialMappings = async (
  queryClient: QueryClient,
  { credentialId }: GetCredentialMappingsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCredentialMappingsQueryKey({ credentialId }) }, options);

  return queryClient;
};

/**
 * @summary Add Credential Mapping
 */
export const addCredentialMapping = (
  { credentialId }: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialMappingResponse>(
    {
      url: `/connector-credentials/${credentialId}/mappings`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAddCredentialMappingRequest,
      signal,
    },
    options,
  );
};

export const getAddCredentialMappingQueryKey = (
  { credentialId }: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest?: BodyType<TLAddCredentialMappingRequest>,
) => {
  return ['POST', 'connector-credentials', credentialId, 'mappings', tLAddCredentialMappingRequest] as const;
};

export const getAddCredentialMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof addCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAddCredentialMappingQueryKey({ credentialId }, tLAddCredentialMappingRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addCredentialMapping>>> = ({ signal }) =>
    addCredentialMapping({ credentialId }, tLAddCredentialMappingRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddCredentialMappingQueryResult = NonNullable<Awaited<ReturnType<typeof addCredentialMapping>>>;
export type AddCredentialMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useAddCredentialMapping<
  TData = Awaited<ReturnType<typeof addCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addCredentialMapping>>,
          TError,
          Awaited<ReturnType<typeof addCredentialMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddCredentialMapping<
  TData = Awaited<ReturnType<typeof addCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addCredentialMapping>>,
          TError,
          Awaited<ReturnType<typeof addCredentialMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddCredentialMapping<
  TData = Awaited<ReturnType<typeof addCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Credential Mapping
 */

export function useAddCredentialMapping<
  TData = Awaited<ReturnType<typeof addCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddCredentialMappingQueryOptions({ credentialId }, tLAddCredentialMappingRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Credential Mapping
 */
export const prefetchAddCredentialMappingQuery = async <
  TData = Awaited<ReturnType<typeof addCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddCredentialMappingQueryOptions({ credentialId }, tLAddCredentialMappingRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Credential Mapping
 */
export const invalidateAddCredentialMapping = async (
  queryClient: QueryClient,
  { credentialId }: AddCredentialMappingPathParameters,
  tLAddCredentialMappingRequest: BodyType<TLAddCredentialMappingRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddCredentialMappingQueryKey({ credentialId }, tLAddCredentialMappingRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remove Credential Mapping
 */
export const removeCredentialMapping = (
  { credentialId, connectionId }: RemoveCredentialMappingPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/connector-credentials/${credentialId}/mappings/${connectionId}`, method: 'DELETE', signal },
    options,
  );
};

export const getRemoveCredentialMappingQueryKey = ({
  credentialId,
  connectionId,
}: RemoveCredentialMappingPathParameters) => {
  return ['DELETE', 'connector-credentials', credentialId, 'mappings', connectionId] as const;
};

export const getRemoveCredentialMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof removeCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId, connectionId }: RemoveCredentialMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemoveCredentialMappingQueryKey({ credentialId, connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeCredentialMapping>>> = ({ signal }) =>
    removeCredentialMapping({ credentialId, connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined && connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemoveCredentialMappingQueryResult = NonNullable<Awaited<ReturnType<typeof removeCredentialMapping>>>;
export type RemoveCredentialMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveCredentialMapping<
  TData = Awaited<ReturnType<typeof removeCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveCredentialMappingPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeCredentialMapping>>,
          TError,
          Awaited<ReturnType<typeof removeCredentialMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveCredentialMapping<
  TData = Awaited<ReturnType<typeof removeCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveCredentialMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeCredentialMapping>>,
          TError,
          Awaited<ReturnType<typeof removeCredentialMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveCredentialMapping<
  TData = Awaited<ReturnType<typeof removeCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveCredentialMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Credential Mapping
 */

export function useRemoveCredentialMapping<
  TData = Awaited<ReturnType<typeof removeCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId, connectionId }: RemoveCredentialMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveCredentialMappingQueryOptions({ credentialId, connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Credential Mapping
 */
export const prefetchRemoveCredentialMappingQuery = async <
  TData = Awaited<ReturnType<typeof removeCredentialMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId, connectionId }: RemoveCredentialMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveCredentialMappingQueryOptions({ credentialId, connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Credential Mapping
 */
export const invalidateRemoveCredentialMapping = async (
  queryClient: QueryClient,
  { credentialId, connectionId }: RemoveCredentialMappingPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemoveCredentialMappingQueryKey({ credentialId, connectionId }) },
    options,
  );

  return queryClient;
};
